/**
 * LandlordSoftwareHub — /landlord-software (no slug) index.
 *
 * Sits between the homepage and the 13 per-province deep pages so:
 *   • Users who type or search the bare URL land on a real page.
 *   • Google can crawl every province from one link-rich hub.
 *   • The page ranks for "property management software Canada" +
 *     "landlord software Canada" + "property manager software".
 *
 * Reuses ProvinceSoftwareGrid for the province index so the same
 * cards render on the homepage AND on this hub — no duplication.
 */
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import ProvinceSoftwareGrid from "@/components/landing/ProvinceSoftwareGrid";
import { usePageTitle } from "@/lib/seo";

export default function LandlordSoftwareHub() {
  usePageTitle(
    "Landlord software Canada — property management for all 13 provinces · Central Rentals",
  );

  return (
    <div className="bg-white text-slate-900 landing-page" data-testid="landlord-software-hub">
      <PublicHeader light />

      <section className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-emerald-700">
            Property management software · Canada
          </div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-slate-950 leading-[1.02] max-w-4xl">
            Landlord software calibrated to your province.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-700 leading-relaxed">
            Every provincial rental law is different. Central Rentals ships a
            purpose-built landing, notice pack, and rent-increase math for
            each of the 13 Canadian jurisdictions — from the LTB in Ontario to
            the RTB in BC to the TAL in Quebec. Pick your province below.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/signup?role=landlord"
              data-testid="hub-primary-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-base font-extrabold shadow-lg shadow-emerald-600/25 transition-colors"
            >
              Start free trial <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/pricing"
              data-testid="hub-secondary-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 hover:border-slate-900 text-slate-900 text-base font-extrabold transition-colors"
            >
              See pricing <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Province grid — reused from the homepage so the same cards
          appear on both places without duplication. */}
      <ProvinceSoftwareGrid />

      {/* CTA repeater at the bottom so scroll-readers convert. */}
      <section className="border-t border-slate-100 bg-emerald-50/50">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 text-center">
          <h2 className="font-display text-2xl md:text-4xl font-extrabold text-slate-950">
            Every province · one login · CAD-native.
          </h2>
          <p className="mt-3 text-slate-700">
            Cancel anytime · No long-term contract · CA$35/month
          </p>
          <div className="mt-6">
            <Link
              to="/signup?role=landlord"
              data-testid="hub-bottom-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-slate-950 hover:bg-slate-800 text-white text-base font-extrabold shadow-lg transition-colors"
            >
              Start free trial <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
